import { useRef, useEffect } from 'react';

/* ─────────────────────────────────────────────────────────────────────────
   NeuronHero — a scroll-driven neural field.

   Faithful port of the "Neura — Nocturne" reference (Claude Design handoff),
   rebuilt for production. The reference is a 2D <canvas> that hand-rolls its
   own 3D perspective projection (Y/X rotation matrices → FOCAL/z divide),
   draws with additive compositing + shadowBlur glow, and pushes the camera
   INTO a seeded neuron cloud as an internal scroll surface advances 0→1.

   Adapted here to:
     • Inveira's palette — a cool, monochromatic BLUE + white/silver field
       (no gold anywhere in the scene; gold lives only on the badge + primary
       button, which are DOM, not canvas).
     • the site's page scroll — Home.jsx pins a tall runway and writes 0→1
       into progressRef; this component reads it (no internal scroller).
     • production concerns — DPR cap, visibility-gated rAF (pauses off-screen),
       prefers-reduced-motion static frame, and a lighter mobile field.

   Overlay copy (badge / headline / CTAs / cue) is real DOM for a11y; its
   opacity is scrubbed in the draw loop via refs — zero React re-renders.
──────────────────────────────────────────────────────────────────────── */

// blue-toned scene palette (r,g,b) — NO gold
const NODE = [76, 130, 247];   // #4C82F7 satellite bodies + dendrites
const CORE = [206, 224, 255];  // light silver-blue node core
const HUB = [236, 244, 255];   // near-white core for the big hub nodes
const LINE = [92, 140, 232];   // connections
const SIG = [232, 242, 255];   // traveling signals (white/light-blue)

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// band(p,a,b,c,d): 0 before a, ramps to 1 over a→b, holds, ramps to 0 over c→d
const band = (p, a, b, c, d) => {
  if (p < a || p > d) return 0;
  if (p < b) return (p - a) / (b - a);
  if (p > c) return 1 - (p - c) / (d - c);
  return 1;
};

/* Build the neuron cloud once (seeded → deterministic layout). */
function buildField(N, R) {
  const rnd = mulberry32(20260715);
  const rn = (a, b) => a + (b - a) * rnd();
  const nodes = [];
  for (let k = 0; k < N; k++) {
    const u = rnd(), v = rnd(), th = 2 * Math.PI * u, ph = Math.acos(2 * v - 1);
    const rad = R * Math.cbrt(rn(0.08, 1)); // cube-root → uniform sphere volume
    const x = rad * Math.sin(ph) * Math.cos(th);
    const y = rad * Math.sin(ph) * Math.sin(th) * 0.82; // gently flattened
    const z = rad * Math.cos(ph);
    const r = rn(1.6, 3.8);
    const tips = [];
    const tn = 2 + Math.floor(rnd() * 4);
    for (let t = 0; t < tn; t++) {
      const d = [rn(-1, 1), rn(-1, 1), rn(-1, 1)];
      const L = Math.hypot(d[0], d[1], d[2]) || 1, len = rn(20, 64);
      tips.push([d[0] / L * len, d[1] / L * len, d[2] / L * len]);
    }
    nodes.push({ x, y, z, r, hub: r > 3.0, ph: rnd() * 6.28, sp: rn(0.3, 1.0), tips });
  }
  // proximity connections: nearest MAXC within TH, de-duplicated
  const TH = R * 0.256, MAXC = 3, conns = [];
  const has = (a, b) => conns.some(c => (c.a === a && c.b === b) || (c.a === b && c.b === a));
  for (let a = 0; a < N; a++) {
    const near = [];
    for (let b = 0; b < N; b++) {
      if (b === a) continue;
      const d = Math.hypot(nodes[a].x - nodes[b].x, nodes[a].y - nodes[b].y, nodes[a].z - nodes[b].z);
      if (d < TH) near.push([d, b]);
    }
    near.sort((p, q) => p[0] - q[0]);
    for (let m = 0; m < Math.min(MAXC, near.length); m++) {
      const b = near[m][1];
      if (!has(a, b)) conns.push({ a, b, active: rnd() < 0.34, off: rnd(), spd: rn(0.16, 0.46) });
    }
  }
  return { nodes, conns };
}

export default function NeuronHero({ progressRef, onPrimary, onSecondary, animate = true, mobile = false, lowPower = false }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const localProgress = useRef(0);
  const prog = progressRef ?? localProgress;

  // overlay refs — opacity scrubbed in the loop (no React re-renders)
  const cueRef = useRef(null);
  const contentRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Quality tier: fewer nodes on phones and low-core (older/weaker) devices —
    // this full-screen additive field is the heaviest scene during the long
    // pinned scroll, so trimming node count is the biggest low-end perf win.
    const R = 460;
    const N = mobile ? 84 : lowPower ? 110 : 168;
    const { nodes, conns } = buildField(N, R);

    // Pre-rendered radial-glow sprites. drawImage of a soft gradient blob is
    // cheap and GPU-friendly — this replaces per-arc shadowBlur (the single
    // most expensive Canvas2D op), which tanked frame rate under load.
    const makeGlow = (rgb) => {
      const s = 64, c = document.createElement('canvas');
      c.width = c.height = s;
      const g = c.getContext('2d');
      const grd = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
      grd.addColorStop(0, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},1)`);
      grd.addColorStop(0.4, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.35)`);
      grd.addColorStop(1, `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0)`);
      g.fillStyle = grd; g.fillRect(0, 0, s, s);
      return c;
    };
    // one soft-blue glow blob + one white signal blob; crisp cores are drawn
    // as hard arcs on top (the reference's bright-dot signature)
    const glowNode = makeGlow(NODE), glowSig = makeGlow(SIG);
    const sprite = (img, x, y, size, alpha) => {
      ctx.globalAlpha = alpha;
      ctx.drawImage(img, x - size / 2, y - size / 2, size, size);
    };

    let W = 0, H = 0;
    // This full-screen additive canvas is fill-rate bound on retina/high-DPI
    // (a Chrome-trace showed the main thread only ~20% busy — the cost is GPU
    // compositing, which scales with pixel count). Cap DPR conservatively; the
    // soft glow scene hides the lower resolution.
    const dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.2 : lowPower ? 1.2 : 1.35);
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      W = r.width || window.innerWidth;
      H = r.height || window.innerHeight;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    let ro;
    if (window.ResizeObserver) { ro = new ResizeObserver(resize); ro.observe(canvas); }

    // pointer parallax (fine pointers only)
    const finePointer = window.matchMedia?.('(pointer: fine)').matches;
    const onMove = (e) => {
      mouse.current.tx = e.clientX / window.innerWidth - 0.5;
      mouse.current.ty = e.clientY / window.innerHeight - 0.5;
    };
    if (finePointer && animate) window.addEventListener('pointermove', onMove, { passive: true });

    const CAM = 720, FOCAL = 620, TRAVEL = 1180, NEAR = 60;
    const proj = (x, y, z, ay, ax, p, mx, my) => {
      const cy = Math.cos(ay), sy = Math.sin(ay);
      const rx = x * cy + z * sy, rz = -x * sy + z * cy, ry = y;
      const cx = Math.cos(ax), sx = Math.sin(ax);
      const ry2 = ry * cx - rz * sx, rz2 = ry * sx + rz * cx;
      const zc = rz2 + CAM - p * TRAVEL;
      if (zc < NEAR) return null;
      const s = FOCAL / zc;
      return { sx: W / 2 + rx * s + mx * 46, sy: H / 2 - ry2 * s + my * 46, s, zc };
    };
    const fogFor = (zc) => 1 - clamp((zc - NEAR) / (CAM + R * 1.6), 0, 1) * 0.82;

    const P = new Array(N);
    const t0 = performance.now();

    const renderFrame = (staticFrame) => {
      // ease scroll target; idle when unpinned (progress stays ~0)
      const target = clamp(prog.current ?? 0, 0, 1);
      // Track the (already Lenis-smoothed) scroll tightly. The old 0.075 lerp
      // added ~0.5s of extra lag on top of Lenis, making the hero fly-through —
      // the first thing users scroll — feel sluggish/floaty. 0.16 stays smooth
      // but responds promptly to scroll input.
      if (staticFrame) localProgress.current = target;
      else localProgress.current += (target - localProgress.current) * 0.16;
      const p = staticFrame ? target : localProgress.current;

      const m = mouse.current;
      if (!staticFrame) { m.x += (m.tx - m.x) * 0.06; m.y += (m.ty - m.y) * 0.06; }
      const time = staticFrame ? 0 : (performance.now() - t0) / 1000;

      const ay = time * 0.05 + p * Math.PI * 1.4 + m.x * 0.5;
      const ax = 0.26 + Math.sin(time * 0.1) * 0.04 + m.y * 0.4;

      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter';

      for (let k = 0; k < N; k++) {
        const n = nodes[k];
        const br = 1 + Math.sin(time * n.sp + n.ph) * 0.02;
        P[k] = proj(n.x * br, n.y * br, n.z * br, ay, ax, p, m.x, m.y);
      }

      // connections
      ctx.lineWidth = 1;
      for (const c of conns) {
        const pa = P[c.a], pb = P[c.b];
        if (!pa || !pb) continue;
        const dep = Math.min(fogFor(pa.zc), fogFor(pb.zc));
        ctx.strokeStyle = rgba(LINE, 0.16 * dep);
        ctx.beginPath(); ctx.moveTo(pa.sx, pa.sy); ctx.lineTo(pb.sx, pb.sy); ctx.stroke();
      }

      // dendrite tips
      for (let k = 0; k < N; k++) {
        const n = nodes[k], pa = P[k];
        if (!pa) continue;
        const dep = fogFor(pa.zc);
        for (const tp of n.tips) {
          const pt = proj(n.x + tp[0], n.y + tp[1], n.z + tp[2], ay, ax, p, m.x, m.y);
          if (!pt) continue;
          ctx.strokeStyle = rgba(NODE, 0.12 * dep);
          ctx.beginPath(); ctx.moveTo(pa.sx, pa.sy); ctx.lineTo(pt.sx, pt.sy); ctx.stroke();
          ctx.fillStyle = rgba(NODE, 0.22 * dep);
          ctx.beginPath(); ctx.arc(pt.sx, pt.sy, Math.max(0.6, pt.s * 0.9), 0, 6.283); ctx.fill();
        }
      }

      // traveling signals (skip on static frame — they imply motion): a soft
      // white glow sprite gliding along each active connection
      if (!staticFrame) {
        for (const c of conns) {
          if (!c.active) continue;
          const pa = P[c.a], pb = P[c.b];
          if (!pa || !pb) continue;
          const tt = (time * c.spd + c.off) % 1;
          const sx = pa.sx + (pb.sx - pa.sx) * tt, sy = pa.sy + (pb.sy - pa.sy) * tt;
          const dep = fogFor((pa.zc + pb.zc) / 2);
          const rs = Math.max(1.1, ((pa.s + pb.s) / 2) * 2.0);
          // soft white glow + a crisp bright core — matches the reference's
          // shadowBlur'd signal dot, but cheaply
          sprite(glowSig, sx, sy, rs * 5.5, 0.85 * dep);
          ctx.globalAlpha = 1;
          ctx.fillStyle = rgba(SIG, 0.95 * dep);
          ctx.beginPath(); ctx.arc(sx, sy, Math.max(0.9, rs * 0.8), 0, 6.283); ctx.fill();
        }
      }

      // nodes, back-to-front. Reference recipe: a broad blue glow (there: flat
      // halo disc + 12px shadowBlur) wrapped around a small, CRISP, near-white
      // core disc. We render the glow as a soft sprite (softer + far cheaper
      // than shadowBlur) and keep the core as a hard arc so it reads as a sharp
      // bright dot, not a fuzzy blob — the detail that was missing before.
      const order = [];
      for (let k = 0; k < N; k++) if (P[k]) order.push(k);
      order.sort((a, b) => P[b].zc - P[a].zc);
      for (const k of order) {
        const n = nodes[k], pp = P[k];
        const dep = fogFor(pp.zc);
        const rr = Math.max(0.9, n.r * pp.s * 1.1);
        sprite(glowNode, pp.sx, pp.sy, rr * 5.4, (n.hub ? 0.6 : 0.48) * dep);
        ctx.globalAlpha = 1;
        ctx.fillStyle = rgba(n.hub ? HUB : CORE, 0.96 * dep);
        ctx.beginPath(); ctx.arc(pp.sx, pp.sy, rr, 0, 6.283); ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      // overlay opacity — content holds then dissolves as the camera enters
      if (cueRef.current) cueRef.current.style.opacity = String(clamp(1 - p * 6, 0, 1));
      if (contentRef.current) {
        const o = staticFrame ? 1 : band(p, -1, 0, 0.24, 0.44); // full at 0, fades 0.24→0.44
        contentRef.current.style.opacity = String(o);
        contentRef.current.style.transform = `translateY(${(1 - o) * -18}px)`;
        contentRef.current.style.pointerEvents = o > 0.5 ? 'auto' : 'none';
      }
    };

    let raf = null;
    // The loop GENUINELY stops when off-screen: it doesn't reschedule itself,
    // so no rAF ticks at all while the hero is out of view (previously it kept
    // ticking every frame forever, just skipping the draw).
    const loop = () => { renderFrame(false); raf = requestAnimationFrame(loop); };
    const start = () => { if (raf == null) raf = requestAnimationFrame(loop); };
    const stop = () => { if (raf != null) { cancelAnimationFrame(raf); raf = null; } };

    if (!animate) {
      renderFrame(true); // prefers-reduced-motion: a single static frame
    } else {
      const io = new IntersectionObserver(([e]) => { e.isIntersecting ? start() : stop(); }, { rootMargin: '120px' });
      io.observe(canvas);
      start();
      return () => {
        stop();
        io.disconnect();
        window.removeEventListener('resize', resize);
        window.removeEventListener('pointermove', onMove);
        ro?.disconnect();
      };
    }
    return () => {
      window.removeEventListener('resize', resize);
      ro?.disconnect();
    };
  }, [animate, mobile, lowPower, prog]);

  return (
    <div ref={wrapRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <canvas ref={canvasRef} aria-hidden="true"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', zIndex: 0 }} />

      {/* blue ambience: a soft glow biased toward the text, plus a vignette */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'radial-gradient(110% 90% at 34% 46%, rgba(40,70,150,0.42) 0%, rgba(7,9,15,0) 60%)' }} />
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'radial-gradient(140% 120% at 50% 50%, rgba(7,9,15,0) 55%, rgba(4,5,10,0.78) 100%)' }} />
      {/* left legibility scrim — text sits over it; the field stays visible
          behind and to the right (docked-left, à la Render.com) */}
      <div aria-hidden="true" className="neuron-scrim" style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: 'linear-gradient(90deg, rgba(7,9,15,0.78) 0%, rgba(7,9,15,0.42) 26%, rgba(7,9,15,0) 54%)' }} />

      {/* content docked left (centered on mobile) — real DOM for a11y */}
      <div className="neuron-content" style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', pointerEvents: 'none' }}>
        <div ref={contentRef} className="neuron-inner" style={{ willChange: 'opacity, transform' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(200,146,74,0.06)',
            border: '1px solid rgba(200,146,74,0.16)', borderRadius: 20, padding: '5px 14px', marginBottom: 26 }}>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#C8924A', opacity: 0.85, display: 'inline-block' }} />
            <span style={{ fontSize: 10.5, color: 'rgba(200,146,74,0.8)', fontWeight: 500, letterSpacing: '.14em', textTransform: 'uppercase' }}>
              AI-powered college interview practice
            </span>
          </div>
          <h1 style={{ margin: 0, fontFamily: '"DM Serif Display",Georgia,serif', fontWeight: 400,
            fontSize: 'clamp(34px, 4.4vw, 62px)', lineHeight: 1.04, letterSpacing: '-.02em', color: '#F3F5FE', textWrap: 'balance' }}>
            An AI that actually knows your school.
          </h1>
          <div className="neuron-ctas" style={{ marginTop: 34, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={onPrimary}
              style={{ background: '#C8924A', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 28px',
                fontFamily: '"DM Sans",-apple-system,sans-serif', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
              Create account
            </button>
            <button onClick={onSecondary}
              style={{ background: 'rgba(7,9,15,0.35)', color: '#8A909C', border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 10, padding: '13px 26px', fontFamily: '"DM Sans",-apple-system,sans-serif', fontSize: 14, cursor: 'pointer' }}>
              Log in
            </button>
          </div>
        </div>
      </div>

      {/* scroll cue */}
      <div ref={cueRef} aria-hidden="true" style={{ position: 'absolute', left: '50%', bottom: 34, transform: 'translateX(-50%)',
        zIndex: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, pointerEvents: 'none' }}>
        <span style={{ fontSize: 11, letterSpacing: '.34em', textTransform: 'uppercase', color: '#8A909C' }}>Scroll to enter</span>
        <span style={{ width: 1, height: 46, background: 'linear-gradient(#4C82F7, rgba(76,130,247,0))' }} />
      </div>
    </div>
  );
}
